import { FC } from 'react'
import { Box, Typography } from '@mui/material'

/* Utils */
import { CompetitorAnalysisPerformanceMetric } from 'models'

/* Components */
import { Mark as IMark } from './CompetitorAnalysisItem'
import {
  IndustryAverageMarker,
  LowPerformersRange,
  Mark,
  MarkContainer,
  RANGE_MIN_HIT_WIDTH,
  SLAMarker,
  TopPerformersRange,
  Wrapper
} from './competitorAnalysisSlider.styles'
import BenchmarkRangeTooltip from './BenchmarkRangeTooltip'

interface Props {
  item: CompetitorAnalysisPerformanceMetric
  measurementUnit: string
  unit: string
  marks: IMark[]
  slaPosition: number
  avgIndustryPosition: number
  lowRangeLeft: number
  lowRangeWidth: number
  topRangeLeft: number
  topRangeWidth: number
  sla?: number
  max: number
  barWidth: number
  barLeft: number
  isReverse: boolean
  active: boolean
}

const CompetitorAnalysisSlider: FC<Props> = ({
  slaPosition,
  avgIndustryPosition,
  item,
  measurementUnit,
  unit,
  marks,
  lowRangeLeft,
  lowRangeWidth,
  topRangeLeft,
  topRangeWidth,
  sla,
  max,
  barLeft,
  isReverse,
  barWidth,
  active
}) => {
  const getTooltipValue = (value: number | string): string => {
    if (unit === 'hours' || unit === 'days') {
      return `${value} ${unit}`
    }
    return `${value}${measurementUnit}`
  }

  // A zero-width range only gets a hit area from its min-width, so pull it back to stay centred on the value.
  const pointOffset = (width: number): string | number => (width === 0 ? `-${RANGE_MIN_HIT_WIDTH / 2}px` : 0)

  return (
    <Wrapper isReverse={isReverse}>
      {/* Industry Average */}
      <BenchmarkRangeTooltip
        title={
          <Box className="tooltip-value">
            <Typography className="tooltip-title">{getTooltipValue(item.summary.industry_performance_avg)}</Typography>
            <Typography className="tooltip-text">
              This is the average SLA used{' '}
              {item.industry_name === 'All Industries'
                ? 'across all industries.'
                : `in the ${item.industry_name} industry`}
            </Typography>
          </Box>
        }
        placement="bottom"
      >
        <IndustryAverageMarker left={avgIndustryPosition} />
      </BenchmarkRangeTooltip>

      {/* SLA */}
      {typeof sla !== 'undefined' && sla <= max && active && (
        <BenchmarkRangeTooltip
          title={
            <Box className="tooltip-value">
              <Typography className="tooltip-title">{getTooltipValue(sla)}</Typography>
              <Typography className="tooltip-text">This is what your SLA is currently set to</Typography>
            </Box>
          }
          placement="bottom"
        >
          <SLAMarker left={slaPosition} />
        </BenchmarkRangeTooltip>
      )}

      <Box className="bar-block" sx={{ width: `${barWidth}%`, marginLeft: `${barLeft}%` }} />

      {/* Low Performers Range */}
      <BenchmarkRangeTooltip
        title={
          <Box className="tooltip-value">
            <Typography className="tooltip-title">
              {getTooltipValue(
                `${item.summary.bottom_performance_range.low} - ${item.summary.bottom_performance_range.high}`
              )}
            </Typography>
            <Typography className="tooltip-text">Low performers set their SLA within this range.</Typography>
          </Box>
        }
        placement="bottom"
      >
        <LowPerformersRange
          left={lowRangeLeft}
          sx={{ width: `${lowRangeWidth}%`, marginLeft: pointOffset(lowRangeWidth) }}
        />
      </BenchmarkRangeTooltip>

      {/* Top Performers Range */}
      <BenchmarkRangeTooltip
        title={
          <Box className="tooltip-value">
            <Typography className="tooltip-title">
              {getTooltipValue(
                `${item.summary.top_performance_range.low} - ${item.summary.top_performance_range.high}`
              )}
            </Typography>
            <Typography className="tooltip-text">Top performers set their SLA within this range.</Typography>
          </Box>
        }
        placement="bottom"
      >
        <TopPerformersRange
          left={topRangeLeft}
          sx={{ width: `${topRangeWidth}%`, marginLeft: pointOffset(topRangeWidth) }}
        />
      </BenchmarkRangeTooltip>

      <MarkContainer>
        {marks.map((mark) => (
          <Mark key={mark.value} left={(mark.value * 100) / max}>
            {mark.label}
          </Mark>
        ))}
      </MarkContainer>
    </Wrapper>
  )
}

export default CompetitorAnalysisSlider
